import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { mergeMap, switchMap, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { InvoicesActions } from './invoices.actions';
import { InvoiceService } from '../../services/invoice.service';

@Injectable()
export class InvoicesEffects {
  private actions$ = inject(Actions);
  private svc = inject(InvoiceService);

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.loadInvoices),
      mergeMap(({ customerId, status }) => this.svc.getAll(customerId, status).pipe(
        map(invoices => InvoicesActions.loadInvoicesSuccess({ invoices })),
        catchError(err => of(InvoicesActions.loadInvoicesFailure({ error: err.message })))
      ))
    )
  );

  loadOne$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.loadInvoice),
      mergeMap(({ id }) => this.svc.getById(id).pipe(
        map(invoice => InvoicesActions.loadInvoiceSuccess({ invoice })),
        catchError(err => of(InvoicesActions.loadInvoiceFailure({ error: err.message })))
      ))
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.createInvoice),
      mergeMap(({ dto }) => this.svc.create(dto).pipe(
        map(invoice => InvoicesActions.createInvoiceSuccess({ invoice })),
        catchError(err => of(InvoicesActions.createInvoiceFailure({ error: err.message })))
      ))
    )
  );

  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.updateInvoiceStatus),
      mergeMap(({ id, dto }) => this.svc.updateStatus(id, dto).pipe(
        map(invoice => InvoicesActions.updateInvoiceStatusSuccess({ invoice })),
        catchError(err => of(InvoicesActions.updateInvoiceStatusFailure({ error: err.message })))
      ))
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.deleteInvoice),
      mergeMap(({ id }) => this.svc.delete(id).pipe(
        map(() => InvoicesActions.deleteInvoiceSuccess({ id })),
        catchError(err => of(InvoicesActions.deleteInvoiceFailure({ error: err.message })))
      ))
    )
  );

  loadVatConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.loadVatConfig),
      mergeMap(() => this.svc.getVatConfig().pipe(
        map(vatConfig => InvoicesActions.loadVatConfigSuccess({ vatConfig })),
        catchError(err => of(InvoicesActions.loadVatConfigFailure({ error: err.message })))
      ))
    )
  );

  updateVatConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.updateVatConfig),
      mergeMap(({ dto }) => this.svc.upsertVatConfig(dto).pipe(
        map(vatConfig => InvoicesActions.updateVatConfigSuccess({ vatConfig })),
        catchError(err => of(InvoicesActions.updateVatConfigFailure({ error: err.message })))
      ))
    )
  );

  loadPartnerLedger$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InvoicesActions.loadPartnerLedger),
      switchMap(({ partnerId, from, to }) => this.svc.getPartnerLedger(partnerId, from, to).pipe(
        map(ledger => InvoicesActions.loadPartnerLedgerSuccess({ ledger })),
        catchError(err => of(InvoicesActions.loadPartnerLedgerFailure({ error: err.message })))
      ))
    )
  );
}
